import React, { useState } from 'react';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { usePatrimonio } from '../hooks/usePatrimonio';
import { formatCurrency } from '../../../shared/utils/currency-utils';
import { formatDate } from '../../../shared/utils/date-utils';
import { Loader } from '../../../shared/components/Loader';
import { ErrorMessage } from '../../../shared/components/ErrorMessage';

export const PatrimonioDashboard: React.FC = () => {
  const {
    patrimonioActual,
    historialPatrimonio,
    balancePorMes,
    desglosePorCategoria,
    proyeccionComparativa,
    loading,
    error
  } = usePatrimonio();

  const [activeTab, setActiveTab] = useState<'balance' | 'categorias' | 'proyecciones'>('balance');

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error} 
        onRetry={() => window.location.reload()} 
      />
    );
  }

  // Preparar datos para gráficos
  const datosHistorial = historialPatrimonio.map(item => ({
    fecha: formatDate(item.fecha),
    patrimonio: item.patrimonioNeto,
    ingresos: item.totalIngresos,
    egresos: item.totalEgresos
  }));

  const datosCategorias = Object.entries(desglosePorCategoria).map(([categoria, monto]) => ({
    categoria,
    monto
  }));

  const datosProyecciones = proyeccionComparativa.conservadora.proyecciones.map((item, index) => ({
    año: item.año,
    conservadora: item.monto,
    moderada: proyeccionComparativa.moderada.proyecciones[index]?.monto || 0,
    agresiva: proyeccionComparativa.agresiva.proyecciones[index]?.monto || 0
  }));

  const COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

  return (
    <div className="space-y-6">
      {/* Resumen del Patrimonio */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Resumen del Patrimonio</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-800">Patrimonio Neto</h3>
            <p className="text-2xl font-bold text-green-900">
              {formatCurrency(patrimonioActual.patrimonioNeto)}
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800">Total Ingresos</h3>
            <p className="text-2xl font-bold text-blue-900">
              {formatCurrency(patrimonioActual.totalIngresos)}
            </p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-red-800">Total Egresos</h3>
            <p className="text-2xl font-bold text-red-900">
              {formatCurrency(patrimonioActual.totalEgresos)}
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-800">Total Inversiones</h3>
            <p className="text-2xl font-bold text-purple-900">
              {formatCurrency(patrimonioActual.totalInversiones)}
            </p>
          </div>
        </div>
      </div>

      {/* Balance del Mes */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Balance del Mes</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <h3 className="text-sm font-medium text-gray-500">Ingresos</h3>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(balancePorMes.ingresos)}
            </p>
          </div>
          <div className="text-center">
            <h3 className="text-sm font-medium text-gray-500">Egresos</h3>
            <p className="text-2xl font-bold text-red-600">
              {formatCurrency(balancePorMes.egresos)}
            </p>
          </div>
          <div className="text-center">
            <h3 className="text-sm font-medium text-gray-500">Balance</h3>
            <p className={`text-2xl font-bold ${balancePorMes.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(balancePorMes.balance)}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs para gráficos */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('balance')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'balance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Balance Histórico
            </button>
            <button
              onClick={() => setActiveTab('categorias')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'categorias'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Gastos por Categoría
            </button>
            <button
              onClick={() => setActiveTab('proyecciones')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'proyecciones'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Proyecciones
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'balance' && (
            <div className="h-96">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Evolución del Patrimonio</h3>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={datosHistorial}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(Number(value)), 'Patrimonio']}
                    labelFormatter={(label) => `Fecha: ${label}`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="patrimonio" 
                    stroke="#3B82F6" 
                    fill="#3B82F6" 
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeTab === 'categorias' && (
            <div className="h-96">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Gastos por Categoría</h3>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={datosCategorias}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ categoria, monto }) => `${categoria}: ${formatCurrency(monto)}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="monto"
                  >
                    {datosCategorias.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatCurrency(Number(value)), 'Monto']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeTab === 'proyecciones' && (
            <div className="h-96">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Proyecciones de Inversión</h3>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={datosProyecciones}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="año" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(Number(value)), 'Monto']}
                    labelFormatter={(label) => `Año: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="conservadora" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    name="Conservadora (3%)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="moderada" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    name="Moderada (6%)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="agresiva" 
                    stroke="#EF4444" 
                    strokeWidth={2}
                    name="Agresiva (10%)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
